/**
 * Pure company feature-flag core (claude/design/feature-flags/).
 *
 * Framework-agnostic, shared by the admin `useFeature` hook, the web
 * `useWebFeature` hook and the route filters. Where an ACT answers "may this
 * user do this?", a feature flag answers "does this company have this
 * module?": effective access is flag ON and ACT held, and the flag is checked
 * first, ahead of every ACT bypass.
 *
 * Fail-open by design: the server is the enforcement, the client only hides.
 */

/** Effective `{key: bool}` set from `/users/me/` or the web config. */
export type FeatureSet = Partial<Record<string, boolean>>;

/**
 * Modules that are OFF unless the set says otherwise: absent means off for
 * the module and every one of its sub-features, on every client
 * (claude/design/whatsapp-messaging/02 §11.5, 99 D14; the same names as
 * dolby's and the POS's `DEFAULT_OFF_FEATURE_MODULES`).
 */
export const DEFAULT_OFF_FEATURE_MODULES: readonly string[] = ["whatsapp"];

/** Whether `key` belongs to a default-off module (`whatsapp`, `whatsapp.otp`). */
export function isDefaultOffFeature(key: string | null | undefined): boolean {
  if (!key) return false;
  return DEFAULT_OFF_FEATURE_MODULES.includes(key.split(".")[0]);
}

/**
 * Whether `key` is on for the company.
 *
 * - `features` null/undefined (an old backend, sage staff with no resolved
 *   company, or a set that has not loaded yet) => on.
 * - A key missing from the set => on.
 * - A sub-feature (`a.b`) is off whenever any ancestor module (`a`) is off,
 *   whatever its own value, so a client never shows a child of a hidden
 *   module even if the payload says otherwise.
 * - Except for a default-off module (`isDefaultOffFeature`): its keys are on
 *   only when the key and every ancestor are explicitly `true`, so a missing
 *   key or a missing set is off.
 */
export function isFeatureEnabled(
  features: FeatureSet | null | undefined,
  key: string | null | undefined,
): boolean {
  if (!key) return true;
  const parts = key.split(".");
  const prefixes = parts.map((_, index) => parts.slice(0, index + 1).join("."));
  if (isDefaultOffFeature(key)) {
    return Boolean(features) && prefixes.every((prefix) => features?.[prefix] === true);
  }
  if (!features) return true;
  return prefixes.every((prefix) => features[prefix] !== false);
}

/** The first of `keys` that is off, or `undefined` when all are on. */
export function firstDisabledFeature(
  features: FeatureSet | null | undefined,
  keys: readonly string[] | null | undefined,
): string | undefined {
  return (keys ?? []).find((key) => !isFeatureEnabled(features, key));
}

/** Message shown where a disabled feature's page or action would be. */
export const FEATURE_DISABLED_MESSAGE =
  "This feature is not enabled for your company";

/** The `code` amphitheater puts in a 403 body for a disabled feature. */
export const FEATURE_DISABLED_CODE = "FEATURE_DISABLED";

/**
 * The feature key a failed request was refused for, or `null` when the error
 * is not a `FEATURE_DISABLED` 403. Accepts either shape an error takes here:
 * the axios response the shared interceptor rejects with (`{status, data}`),
 * or the response body an RTK Query `axiosBaseQuery` error carries
 * (`{code, feature}`).
 */
export function getFeatureDisabledKey(error: unknown): string | null {
  const candidate = error as {
    status?: number;
    code?: unknown;
    feature?: unknown;
    data?: { code?: unknown; feature?: unknown } | null;
  } | null;
  if (!candidate || typeof candidate !== "object") return null;
  const body = candidate.data ?? candidate;
  if (candidate.data && candidate.status !== 403) return null;
  if (body.code === FEATURE_DISABLED_CODE && typeof body.feature === "string") {
    return body.feature;
  }
  return null;
}
